<template>
  <div class="auth-page">
    <navbar />

    <main class="auth-main">
      <section class="auth-panel" :class="{ 'is-register': isRegister }" aria-live="polite">
        <div class="auth-panel-topline"></div>

        <!-- <div class="auth-kicker">GixCore Account</div> -->
        <h1 class="auth-title">{{ pageTitle }}</h1>
        <p class="auth-copy">{{ pageCopy }}</p>

        <div v-if="visibleRouteConfirmationNotice" class="auth-message" :class="'is-' + visibleRouteConfirmationNotice.type" role="status">
          <span class="auth-message-title">{{ visibleRouteConfirmationNotice.title }}</span>
          <span>{{ visibleRouteConfirmationNotice.text }}</span>
        </div>

        <div v-if="authNotice" class="auth-message" :class="'is-' + authNotice.type" role="status">
          <span class="auth-message-title">{{ authNotice.title }}</span>
          <span>{{ authNotice.text }}</span>
          <div v-if="showNoticeResendAction" class="auth-message-action">
            <button type="button" :disabled="resendLoading || resendCooldownSeconds > 0" @click="resendLoginConfirmation">
              {{ noticeResendLabel }}
            </button>
          </div>
        </div>

        <form class="auth-form" v-on:submit.prevent="submitAuthForm">
          <div v-if="isRegister" class="auth-field">
            <label for="username">Username</label>
            <input
              id="username"
              v-model.trim="username"
              class="auth-input"
              :class="{ 'is-invalid': hasFieldError('username') }"
              type="text"
              autocomplete="username"
              placeholder="builder name"
              @input="clearFieldError('username')"
              @blur="markTouched('username')"
              required
            >
            <span v-if="fieldValidationMessage('username')" class="auth-field-error">
              {{ fieldValidationMessage('username') }}
            </span>
          </div>

          <div class="auth-field">
            <label for="email">Email address</label>
            <input
              id="email"
              v-model.trim="email"
              class="auth-input"
              :class="{ 'is-invalid': hasFieldError('email') }"
              type="email"
              autocomplete="email"
              placeholder="you@example.com"
              @input="handleFieldInput('email')"
              @blur="markTouched('email')"
              required
            >
            <span v-if="fieldValidationMessage('email')" class="auth-field-error">
              {{ fieldValidationMessage('email') }}
            </span>
          </div>

          <div v-if="isRegister" class="auth-name-grid">
            <div class="auth-field">
              <label for="first_name">First name</label>
              <input
                id="first_name"
                v-model.trim="first_name"
                class="auth-input"
                :class="{ 'is-invalid': hasFieldError('first_name') }"
                type="text"
                autocomplete="given-name"
                placeholder="First"
                @input="clearFieldError('first_name')"
                @blur="markTouched('first_name')"
                required
              >
              <span v-if="fieldValidationMessage('first_name')" class="auth-field-error">
                {{ fieldValidationMessage('first_name') }}
              </span>
            </div>

            <div class="auth-field">
              <label for="last_name">Last name</label>
              <input
                id="last_name"
                v-model.trim="last_name"
                class="auth-input"
                :class="{ 'is-invalid': hasFieldError('last_name') }"
                type="text"
                autocomplete="family-name"
                placeholder="Last"
                @input="clearFieldError('last_name')"
                @blur="markTouched('last_name')"
                required
              >
              <span v-if="fieldValidationMessage('last_name')" class="auth-field-error">
                {{ fieldValidationMessage('last_name') }}
              </span>
            </div>
          </div>

          <div class="auth-field">
            <label for="password">Password</label>
            <input
              id="password"
              v-model="password"
              class="auth-input"
              :class="{ 'is-invalid': hasFieldError('password') }"
              type="password"
              :autocomplete="isRegister ? 'new-password' : 'current-password'"
              placeholder="password"
              @input="handleFieldInput('password')"
              @blur="markTouched('password')"
              required
            >
            <span v-if="fieldValidationMessage('password')" class="auth-field-error">
              {{ fieldValidationMessage('password') }}
            </span>
          </div>

          <div v-if="isRegister" class="auth-field">
            <label for="password_confirm">Confirm password</label>
            <input
              id="password_confirm"
              v-model="password_confirm"
              class="auth-input"
              :class="{ 'is-invalid': hasFieldError('password_confirm') }"
              type="password"
              autocomplete="new-password"
              placeholder="confirm password"
              @input="clearFieldError('password_confirm')"
              @blur="markTouched('password_confirm')"
              required
            >
            <span v-if="fieldValidationMessage('password_confirm')" class="auth-field-error">
              {{ fieldValidationMessage('password_confirm') }}
            </span>
          </div>

          <div v-if="showInlineAuthError" class="auth-error" :class="{ 'is-warning': showResendConfirmation }" role="alert">
            <span class="auth-error-title">{{ authError }}</span>
            <span v-if="authErrorFields.length" class="auth-error-detail">
              Check {{ authErrorFieldLabels }}.
            </span>
            <div v-if="showResendConfirmation" class="auth-resend-inline">
              <span>Need another confirmation email?</span>
              <button type="button" :disabled="resendLoading" @click="resendLoginConfirmation">
                {{ resendButtonLabel }}
              </button>
            </div>
          </div>

          <div class="auth-field auth-captcha">
            <TurnstileWidget
              ref="captcha"
              :action="isRegister ? 'register' : 'login'"
              @verified="onCaptchaVerified"
              @expired="onCaptchaExpired"
              @error="onCaptchaError"
            />
          </div>

          <div class="auth-actions">
            <button class="auth-submit" :class="{ 'is-loading': loading }" type="submit" :disabled="submitDisabled">
              {{ submitLabel }}
            </button>
          </div>
        </form>

        <div class="auth-divider"><span>or</span></div>

        <GoogleSignInButton @credential="onGoogleCredential" @error="onGoogleError" />

        <div class="auth-links" :class="{ 'is-login': !isRegister }">
          <a v-if="!isRegister" class="auth-link-muted" href="#" v-on:click.prevent="handleForgotPassword">
            {{ forgotLoading ? 'Sending…' : 'Forgot password' }}
          </a>
          <router-link v-if="!isRegister" class="auth-link-strong" to="/users/register">Create account</router-link>
          <router-link v-if="isRegister" class="auth-link-strong" to="/users/login">Already have an account?</router-link>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import navbar from '../navbar.vue'
import GoogleSignInButton from './GoogleSignInButton.vue'
import TurnstileWidget from './TurnstileWidget.vue'
import {
  confirmationResendNotice,
  cooldownButtonLabel,
  parseConfirmationResendResult,
} from '../../emailConfirmation'

export default {
  name: 'Register',
  components: {
    navbar,
    GoogleSignInButton,
    TurnstileWidget,
  },
  data() {
    return {
      loading: false,
      resendLoading: false,
      forgotLoading: false,
      authError: '',
      authErrorCode: '',
      authErrorFields: [],
      authNotice: null,
      loginNeedsConfirmation: false,
      resendEmail: '',
      resendCooldownSeconds: 0,
      resendCooldownTimer: null,
      submitted: false,
      captchaToken: '',
      touched: {
        username: false,
        email: false,
        first_name: false,
        last_name: false,
        password: false,
        password_confirm: false,
      },
      email: '',
      password: '',
      password_confirm: '',
      username: '',
      first_name: '',
      last_name: '',
    }
  },
  computed: {
    isRegister() {
      return this.$route.name == 'Register'
    },
    pageTitle() {
      return this.isRegister ? 'Create your account' : 'Sign in'
    },
    pageCopy() {
      return this.isRegister
        ? 'Save builds, compare parts, and keep your hardware shortlist ready.'
        : 'Pick up where your last build left off.'
    },
    submitLabel() {
      if (this.loading) {
        return this.isRegister ? 'Creating...' : 'Signing in...'
      }

      if (this.loginBlockedByConfirmation) {
        return 'Confirm Email First'
      }

      return this.isRegister ? 'Create Account' : 'Log In'
    },
    authErrorFieldLabels() {
      return this.authErrorFields.map((field) => this.fieldLabel(field)).join(', ')
    },
    formFields() {
      return this.isRegister
        ? ['username', 'email', 'first_name', 'last_name', 'password', 'password_confirm']
        : ['email', 'password']
    },
    validationErrors() {
      const errors = {}

      if (this.isRegister && !this.username) {
        errors.username = 'Username is required.'
      }

      if (!this.email) {
        errors.email = 'Email address is required.'
      } else if (!this.validEmail(this.email)) {
        errors.email = 'Enter a valid email address.'
      }

      if (this.isRegister && !this.first_name) {
        errors.first_name = 'First name is required.'
      }

      if (this.isRegister && !this.last_name) {
        errors.last_name = 'Last name is required.'
      }

      if (!this.password) {
        errors.password = 'Password is required.'
      }

      if (this.isRegister && !this.password_confirm) {
        errors.password_confirm = 'Confirm your password.'
      } else if (this.isRegister && this.password && this.password_confirm !== this.password) {
        errors.password_confirm = 'Passwords do not match.'
      }

      return errors
    },
    formValid() {
      return Object.keys(this.validationErrors).length === 0
    },
    loginBlockedByConfirmation() {
      return !this.isRegister && this.loginNeedsConfirmation
    },
    submitDisabled() {
      return this.loading || !this.formValid || this.loginBlockedByConfirmation || !this.captchaToken
    },
    routeConfirmationNotice() {
      if (this.isRegister || !this.$route || !this.$route.query) return null
      const confirmed = this.$route.query.confirmed

      if (confirmed === '1') {
        return {
          type: 'success',
          title: 'Email confirmed.',
          text: 'You can sign in now.',
        }
      }

      if (confirmed === 'invalid') {
        return {
          type: 'warning',
          title: 'Confirmation link is invalid or expired.',
          text: 'Try resending the confirmation email, then open the newest link from your inbox.',
        }
      }

      return null
    },
    visibleRouteConfirmationNotice() {
      if (this.authNotice || this.authError) return null
      return this.routeConfirmationNotice
    },
    showResendConfirmation() {
      return !this.isRegister && this.loginNeedsConfirmation
    },
    showInlineAuthError() {
      return !!this.authError && !this.authNotice
    },
    resendButtonLabel() {
      return this.resendLoading ? 'Sending...' : 'Resend confirmation'
    },
    showNoticeResendAction() {
      return !this.isRegister && this.authNotice && this.authNotice.action === 'confirmation-resend'
    },
    noticeResendLabel() {
      if (this.resendLoading) {
        return 'Sending...'
      }
      if (this.resendCooldownSeconds > 0) {
        return 'Resend in ' + cooldownButtonLabel(this.resendCooldownSeconds)
      }
      return 'Resend confirmation'
    },
  },
  watch: {
    '$route.name'() {
      this.clearAuthFeedback()
    },
  },
  beforeDestroy() {
    this.stopResendCooldown()
  },
  methods: {
    submitAuthForm() {
      this.submitted = true
      this.touchFormFields()

      if (this.loginBlockedByConfirmation) {
        return
      }

      if (!this.formValid) {
        this.authError = 'Check the highlighted fields before continuing.'
        this.authErrorCode = ''
        this.authErrorFields = []
        return
      }

      if (this.isRegister) {
        this.handleRegFormSubmit()
      } else {
        this.handleLoginFormSubmit()
      }
    },
    onGoogleCredential(credential) {
      this.loading = true
      this.authError = ''
      this.authErrorCode = ''
      this.authErrorFields = []
      this.authNotice = null

      this.$store.dispatch('googleLogin', credential)
        .then(() => {
          this.loading = false
        })
        .catch((err) => {
          this.loading = false
          this.setAuthError(err, 'Could not sign in with Google. Please try again.')
        })
    },
    onGoogleError(err) {
      this.authNotice = {
        type: 'warning',
        title: 'Google sign-in unavailable.',
        text: err && err.message ? err.message : 'Use email and password for now.',
      }
    },
    handleRegFormSubmit() {
      this.loading = true
      this.authError = ''
      this.authErrorCode = ''
      this.authErrorFields = []
      this.authNotice = null

      this.$store.dispatch('register', {
        username: this.username,
        email: this.email,
        password: this.password,
        password_confirm: this.password_confirm,
        first_name: this.first_name,
        last_name: this.last_name,
        captchaToken: this.captchaToken,
      })
        .then(() => {
          this.loading = false
        })
        .catch((err) => {
          this.loading = false
          // Turnstile tokens are single-use — get a fresh one before another attempt.
          this.resetCaptcha()
          this.setAuthError(err, 'Could not create the account. Please check the fields and try again.')
        })
    },
    handleLoginFormSubmit() {
      this.loading = true
      this.authError = ''
      this.authErrorCode = ''
      this.authErrorFields = []
      this.authNotice = null
      this.loginNeedsConfirmation = false
      this.resendEmail = this.email

      this.$store.dispatch('login', {
        email: this.email,
        password: this.password,
        captchaToken: this.captchaToken,
      })
        .then(() => {
          this.loading = false
        })
        .catch((err) => {
          this.loading = false
          // Turnstile tokens are single-use — get a fresh one before another attempt.
          this.resetCaptcha()
          this.setAuthError(err, 'Could not sign in. Check your email and password.')
        })
    },
    setAuthError(err, fallback) {
      const error = this.normalizeAuthError(err, fallback)
      this.authError = error.message
      this.authErrorCode = error.code
      this.authErrorFields = error.fields

      if (error.code === 'EMAIL_NOT_CONFIRMED') {
        this.loginNeedsConfirmation = true
        this.resendEmail = this.email
      }
    },
    normalizeAuthError(err, fallback) {
      const data = err && err.response && err.response.data ? err.response.data : null

      if (data) {
        const errorBody = data.error && typeof data.error === 'object' ? data.error : {}
        const code = data.code || data.errorCode || errorBody.code || ''
        const isUnconfirmed = code === 'EMAIL_NOT_CONFIRMED'
        const errorMessage = typeof data.error === 'string' ? data.error : ''

        return {
          message: isUnconfirmed
            ? 'Confirm your email before signing in.'
            : data.message || errorBody.message || errorMessage || fallback,
          code: code,
          fields: Array.isArray(data.fields) ? data.fields : [],
        }
      }

      if (err && err.message) {
        return {
          message: err.message,
          code: '',
          fields: [],
        }
      }

      return {
        message: fallback,
        code: '',
        fields: [],
      }
    },
    resendLoginConfirmation() {
      if (this.resendCooldownSeconds > 0) {
        return
      }

      const email = this.resendEmail || this.email

      if (!email) {
        this.authNotice = {
          type: 'warning',
          title: 'Email required.',
          text: 'Enter the email address you registered with, then try again.',
        }
        return
      }

      this.resendLoading = true
      if (!this.showNoticeResendAction) {
        this.authNotice = null
      }

      this.$store.dispatch('resendConfirmation', { email: email })
        .then((response) => {
          this.resendLoading = false
          this.applyResendOutcome(parseConfirmationResendResult(response))
        })
        .catch((err) => {
          this.resendLoading = false
          this.applyResendOutcome(parseConfirmationResendResult(err))
        })
    },
    applyResendOutcome(outcome) {
      const notice = confirmationResendNotice(outcome)
      this.authError = ''
      this.authErrorCode = ''
      this.authErrorFields = []
      this.loginNeedsConfirmation = true
      this.authNotice = notice

      if (notice.cooldownSeconds > 0) {
        this.startResendCooldown(notice.cooldownSeconds)
      } else {
        this.stopResendCooldown()
      }
    },
    handleForgotPassword() {
      if (this.forgotLoading) return

      const email = this.email

      if (!email || !this.validEmail(email)) {
        this.authNotice = {
          type: 'warning',
          title: 'Enter your email first.',
          text: 'Type the email address for your account above, then tap “Forgot password”.',
        }
        return
      }

      if (!this.captchaToken) {
        this.authNotice = {
          type: 'warning',
          title: 'Complete the security check.',
          text: 'Confirm you’re human above, then tap “Forgot password”.',
        }
        return
      }

      this.forgotLoading = true
      this.authError = ''
      this.authNotice = null

      this.$store.dispatch('forgotPassword', { email: email, captchaToken: this.captchaToken })
        .then(() => {
          this.forgotLoading = false
          // Single-use token: clear it so a follow-up action requests a fresh challenge.
          this.resetCaptcha()
          // The endpoint responds generically, so we never confirm the address exists.
          this.authNotice = {
            type: 'success',
            title: 'Check your inbox.',
            text: 'If an account exists for that email, we sent a link to reset or create your password.',
          }
        })
        .catch((err) => {
          this.forgotLoading = false
          this.resetCaptcha()
          const error = this.normalizeAuthError(err, 'We could not start the reset right now. Try again in a moment.')
          this.authNotice = {
            type: 'warning',
            title: 'Could not send the email.',
            text: error.message,
          }
        })
    },
    onCaptchaVerified(token) {
      this.captchaToken = token || ''
    },
    onCaptchaExpired() {
      this.captchaToken = ''
    },
    onCaptchaError() {
      this.captchaToken = ''
    },
    resetCaptcha() {
      this.captchaToken = ''
      if (this.$refs.captcha) {
        this.$refs.captcha.reset()
      }
    },
    clearAuthFeedback() {
      this.stopResendCooldown()
      this.resetCaptcha()
      this.authError = ''
      this.authErrorCode = ''
      this.authErrorFields = []
      this.authNotice = null
      this.loginNeedsConfirmation = false
      this.submitted = false
      this.touched = {
        username: false,
        email: false,
        first_name: false,
        last_name: false,
        password: false,
        password_confirm: false,
      }
    },
    hasFieldError(field) {
      return this.authErrorFields.indexOf(field) !== -1 || !!this.fieldValidationMessage(field)
    },
    markTouched(field) {
      this.$set(this.touched, field, true)
    },
    touchFormFields() {
      this.formFields.forEach((field) => this.markTouched(field))
    },
    clearFieldError(field) {
      if (this.authErrorFields.indexOf(field) === -1) return
      this.authErrorFields = this.authErrorFields.filter((item) => item !== field)
    },
    handleFieldInput(field) {
      this.clearFieldError(field)

      if (!this.isRegister && this.loginNeedsConfirmation && (field === 'email' || field === 'password')) {
        this.stopResendCooldown()
        this.authError = ''
        this.authErrorCode = ''
        this.authErrorFields = []
        this.authNotice = null
        this.loginNeedsConfirmation = false
      }
    },
    startResendCooldown(seconds) {
      this.stopResendCooldown()
      this.resendCooldownSeconds = Math.max(1, Math.ceil(Number(seconds) || 60))
      this.resendCooldownTimer = window.setInterval(() => {
        if (this.resendCooldownSeconds <= 1) {
          this.stopResendCooldown()
          return
        }
        this.resendCooldownSeconds -= 1
      }, 1000)
    },
    stopResendCooldown() {
      if (this.resendCooldownTimer) {
        window.clearInterval(this.resendCooldownTimer)
        this.resendCooldownTimer = null
      }
      this.resendCooldownSeconds = 0
    },
    fieldValidationMessage(field) {
      if (!this.submitted && !this.touched[field]) return ''
      return this.validationErrors[field] || ''
    },
    validEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    },
    fieldLabel(field) {
      const labels = {
        username: 'username',
        email: 'email address',
        first_name: 'first name',
        last_name: 'last name',
        password: 'password',
        password_confirm: 'confirm password',
      }

      return labels[field] || field
    },
  },
}
</script>

<style scoped>
@import '../../assets/css/custom.css';

.auth-page {
	position: relative;
	min-height: 100vh;
	overflow: hidden;
	background: #080a0f;
	color: #cfd2da;
}

.auth-page::before {
	position: fixed;
	inset: 0;
	z-index: 0;
	background:
		linear-gradient(90deg, rgba(4, 6, 10, 0.86) 0%, rgba(6, 8, 12, 0.74) 46%, rgba(4, 6, 10, 0.92) 100%),
		linear-gradient(180deg, rgba(8, 10, 15, 0.2) 0%, rgba(8, 10, 15, 0.92) 100%),
		url('~@/assets/img/builder-bg.png');
	background-position: center;
	background-repeat: no-repeat;
	background-size: cover;
	content: "";
}

.auth-page::after {
	position: fixed;
	right: 0;
	bottom: 0;
	left: 0;
	z-index: 0;
	height: 34vh;
	background: linear-gradient(180deg, rgba(8, 10, 15, 0), rgba(8, 10, 15, 0.9));
	content: "";
	pointer-events: none;
}

.auth-main {
	position: relative;
	z-index: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: calc(100vh - 72px);
	padding: 1rem 1.25rem 3rem;
}

.auth-panel {
	position: relative;
	width: min(100%, 420px);
	padding: 1.25rem;
	border: 1px solid rgba(67, 72, 87, 0.95);
	border-radius: 6px;
	background:
		linear-gradient(180deg, rgba(25, 27, 32, 0.94), rgba(13, 15, 22, 0.96)),
		rgba(25, 27, 32, 0.94);
	box-shadow:
		0 22px 70px rgba(0, 0, 0, 0.42),
		inset 0 0 0 1px rgba(255, 255, 255, 0.025);
}

.auth-panel::before {
	position: absolute;
	inset: 9px;
	z-index: -1;
	border: 1px solid rgba(25, 151, 198, 0.09);
	border-radius: 4px;
	content: "";
}

.auth-panel-topline {
	position: absolute;
	top: -1px;
	right: 12px;
	left: 12px;
	height: 1px;
	background: linear-gradient(90deg, rgba(25, 151, 198, 0), rgba(25, 151, 198, 0.85), rgba(25, 151, 198, 0));
}

.auth-kicker {
	margin-bottom: 0.35rem;
	color: #1997c6;
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.9px;
	line-height: 1;
	text-transform: uppercase;
}

.auth-title {
	margin: 0;
	color: #cfd2da;
	font-size: 25px;
	font-weight: 500;
	letter-spacing: 0;
	line-height: 1.18;
}

.auth-copy {
	max-width: 340px;
	margin: 0.55rem 0 1rem;
	color: #9198aa;
	font-size: 14px;
	line-height: 1.45;
}

.auth-message {
	display: grid;
	gap: 0.2rem;
	margin: 0 0 1rem;
	padding: 0.75rem 0.85rem;
	border: 1px solid rgba(25, 151, 198, 0.48);
	border-radius: 6px;
	background: rgba(25, 151, 198, 0.08);
	color: #bcd5df;
	font-size: 13px;
	line-height: 1.4;
}

.auth-message-title {
	color: #dceaf0;
	font-weight: 800;
}

.auth-message.is-success {
	border-color: rgba(0, 188, 140, 0.5);
	background: rgba(0, 188, 140, 0.08);
	color: #bfe9df;
}

.auth-message.is-success .auth-message-title {
	color: #9ff0d8;
}

.auth-message.is-warning {
	border-color: rgba(231, 195, 82, 0.5);
	background: rgba(231, 195, 82, 0.08);
	color: #e7d79e;
}

.auth-message.is-warning .auth-message-title {
	color: #f1dc83;
}

.auth-message.is-error {
	border-color: rgba(230, 71, 89, 0.55);
	background: rgba(230, 71, 89, 0.09);
	color: #f08a95;
}

.auth-message.is-error .auth-message-title {
	color: #ff9aa5;
}

.auth-message-action {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	margin-top: 0.35rem;
	padding-top: 0.55rem;
	border-top: 1px solid rgba(0, 188, 140, 0.2);
}

.auth-message-action button {
	min-height: 28px;
	padding: 0 0.7rem;
	border: 1px solid rgba(0, 188, 140, 0.66);
	border-radius: 5px;
	background: rgba(0, 188, 140, 0.08);
	color: #9ff0d8;
	cursor: pointer;
	font-size: 10.5px;
	font-weight: 800;
	letter-spacing: 0.35px;
	text-transform: uppercase;
	transition: border-color 160ms ease, background 160ms ease, color 160ms ease;
}

.auth-message-action button:hover,
.auth-message-action button:focus {
	border-color: rgba(0, 188, 140, 0.95);
	background: rgba(0, 188, 140, 0.15);
	color: #d9fff3;
}

.auth-message-action button:disabled {
	border-color: rgba(67, 72, 87, 0.76);
	background: rgba(67, 72, 87, 0.22);
	color: #8b948f;
	cursor: not-allowed;
}

.auth-message.is-warning .auth-message-action {
	border-top-color: rgba(231, 195, 82, 0.24);
}

.auth-message.is-warning .auth-message-action button {
	border-color: rgba(231, 195, 82, 0.72);
	background: rgba(231, 195, 82, 0.08);
	color: #f1dc83;
}

.auth-message.is-error .auth-message-action {
	border-top-color: rgba(230, 71, 89, 0.22);
}

.auth-message.is-error .auth-message-action button {
	border-color: rgba(230, 71, 89, 0.66);
	background: rgba(230, 71, 89, 0.08);
	color: #ff9aa5;
}

.auth-message.is-warning .auth-message-action button:disabled,
.auth-message.is-error .auth-message-action button:disabled {
	border-color: rgba(67, 72, 87, 0.76);
	background: rgba(67, 72, 87, 0.22);
	color: #8b948f;
}

.auth-form {
	display: grid;
	gap: 0.7rem;
}

.auth-name-grid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 0.6rem;
}

.auth-field {
	display: grid;
	gap: 0.3rem;
}

.auth-field label {
	margin: 0;
	color: #b2b3b4;
	font-size: 12px;
	font-weight: 600;
	letter-spacing: 0.2px;
	line-height: 1;
}

.auth-input {
	width: 100%;
	min-height: 39px;
	padding: 0.55rem 0.72rem;
	border: 1px solid rgba(67, 72, 87, 0.96);
	border-radius: 6px;
	outline: none;
	background: rgba(6, 8, 12, 0.78);
	color: #e5e8ef;
	font-size: 15px;
	letter-spacing: 0;
	transition: border-color 160ms ease, box-shadow 160ms ease, background 160ms ease;
}

.auth-input::placeholder {
	color: rgba(145, 152, 170, 0.58);
}

.auth-input:hover {
	border-color: rgba(25, 151, 198, 0.55);
}

.auth-input:focus {
	border-color: #1997c6;
	background: rgba(8, 10, 15, 0.98);
	box-shadow: 0 0 0 3px rgba(25, 151, 198, 0.11);
}

.auth-input.is-invalid {
	border-color: rgba(230, 71, 89, 0.9);
	box-shadow: 0 0 0 3px rgba(230, 71, 89, 0.1);
}

.auth-field-error {
	color: #f08a95;
	font-size: 11.5px;
	font-weight: 650;
	line-height: 1.3;
}

.auth-error {
	display: grid;
	gap: 0.25rem;
	padding: 0.75rem 0.85rem;
	border: 1px solid rgba(230, 71, 89, 0.55);
	border-radius: 6px;
	background: rgba(230, 71, 89, 0.09);
	color: #f08a95;
	font-size: 13px;
	line-height: 1.4;
}

.auth-error-title {
	color: #ff9aa5;
	font-weight: 700;
}

.auth-error-detail {
	color: #cfd2da;
}

/* "Confirm your email" is a pending state, not a failure — show it amber. */
.auth-error.is-warning {
	border-color: rgba(231, 195, 82, 0.5);
	background: rgba(231, 195, 82, 0.09);
	color: #e7d79e;
}

.auth-error.is-warning .auth-error-title {
	color: #f1dc83;
}

.auth-error.is-warning .auth-resend-inline {
	border-top-color: rgba(231, 195, 82, 0.26);
}

.auth-error.is-warning .auth-resend-inline button {
	border-color: rgba(231, 195, 82, 0.9);
	color: #f1dc83;
}

.auth-error.is-warning .auth-resend-inline button:hover,
.auth-error.is-warning .auth-resend-inline button:focus {
	background: rgba(231, 195, 82, 0.16);
	color: #fbe8a8;
}

.auth-resend-inline {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.75rem;
	margin-top: 0.55rem;
	padding-top: 0.65rem;
	border-top: 1px solid rgba(230, 71, 89, 0.24);
}

.auth-resend-inline span {
	color: #cfd2da;
}

.auth-resend-inline button {
	flex: 0 0 auto;
	min-height: 30px;
	padding: 0 0.75rem;
	border: 1px solid rgba(25, 151, 198, 0.9);
	border-radius: 5px;
	background: rgba(8, 20, 30, 0.72);
	color: #16b4ed;
	cursor: pointer;
	font-size: 11px;
	font-weight: 800;
	letter-spacing: 0.35px;
	text-transform: uppercase;
	transition: border-color 160ms ease, background 160ms ease, color 160ms ease;
}

.auth-resend-inline button:hover,
.auth-resend-inline button:focus {
	background: rgba(25, 151, 198, 0.16);
	color: #dff8ff;
}

.auth-resend-inline button:disabled {
	border-color: rgba(67, 72, 87, 0.96);
	background: rgba(67, 72, 87, 0.26);
	color: #9198aa;
	cursor: progress;
}

.auth-actions {
	margin-top: 0.1rem;
}

.auth-submit {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	min-height: 39px;
	border: 1px solid #16b4ed;
	border-radius: 6px;
	background: linear-gradient(180deg, rgba(25, 151, 198, 0.34), rgba(12, 66, 88, 0.64));
	box-shadow:
		0 0 24px rgba(25, 151, 198, 0.13),
		inset 0 0 18px rgba(25, 151, 198, 0.12);
	color: #f4f8fb;
	cursor: pointer;
	font-size: 13px;
	font-weight: 800;
	letter-spacing: 0.6px;
	line-height: 1;
	text-transform: uppercase;
	transition: background 160ms ease, box-shadow 160ms ease, color 160ms ease, transform 160ms ease;
}

.auth-submit:hover,
.auth-submit:focus {
	background: linear-gradient(180deg, rgba(25, 151, 198, 0.48), rgba(13, 88, 117, 0.78));
	box-shadow:
		0 0 30px rgba(25, 151, 198, 0.22),
		inset 0 0 18px rgba(25, 151, 198, 0.18);
	color: #ffffff;
}

.auth-submit:active {
	transform: translateY(1px);
}

.auth-submit:disabled {
	border-color: rgba(67, 72, 87, 0.96);
	background: rgba(67, 72, 87, 0.35);
	box-shadow: none;
	color: #9198aa;
	cursor: not-allowed;
	transform: none;
}

.auth-submit.is-loading:disabled {
	cursor: progress;
}

.auth-divider {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	margin: 0.85rem 0 0.75rem;
	color: #6b7180;
	font-size: 11px;
	font-weight: 700;
	letter-spacing: 0.8px;
	text-transform: uppercase;
}

.auth-divider::before,
.auth-divider::after {
	flex: 1;
	height: 1px;
	background: rgba(67, 72, 87, 0.86);
	content: "";
}

.auth-links {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: space-between;
	gap: 0.65rem;
	margin-top: 0.85rem;
	padding-top: 0.78rem;
	border-top: 1px solid rgba(67, 72, 87, 0.48);
}

.auth-links a {
	color: #b2b3b4;
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.45px;
	text-transform: uppercase;
	text-decoration: none;
	transition: border-color 160ms ease, color 160ms ease, background 160ms ease;
}

.auth-links a:hover,
.auth-links a:focus {
	color: #1997c6;
	text-decoration: none;
}

.auth-link-muted {
	color: #8f98aa !important;
}

.auth-link-muted:hover,
.auth-link-muted:focus {
	color: #cfd2da !important;
}

.auth-link-strong {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-height: 30px;
	padding: 0 0.7rem;
	border: 1px solid rgba(25, 151, 198, 0.66);
	border-radius: 5px;
	background: rgba(8, 20, 30, 0.4);
	color: #16b4ed !important;
}

.auth-link-strong:hover,
.auth-link-strong:focus {
	border-color: rgba(25, 151, 198, 0.95);
	background: rgba(25, 151, 198, 0.13);
	color: #dff8ff !important;
}

@media screen and (max-width: 640px) {
	.auth-main {
		align-items: stretch;
		min-height: calc(100vh - 72px);
		padding: 0.6rem 1rem 1rem;
	}

	.auth-panel {
		display: flex;
		flex-direction: column;
		justify-content: center;
		width: 100%;
		padding: 1rem;
	}

	.auth-title {
		font-size: 23px;
	}

	.auth-name-grid {
		grid-template-columns: 1fr;
	}

	.auth-links {
		align-items: stretch;
		flex-direction: column;
	}

	.auth-link-strong {
		width: 100%;
	}

	.auth-resend-inline {
		align-items: flex-start;
		flex-direction: column;
	}
}
</style>
